/**
 * TaskDetail - a task as a canvas with panes floating over its edges.
 *
 * WHAT CHANGED, AND WHY.
 *
 * The page was a `FilterBarWithTabs` over a `h-[calc(100vh-150px)]` scroller:
 * a hard-coded guess at the height of everything above it, wrong on any
 * viewport where the header wrapped. The audit log was worse - a hand-rolled
 * `fixed inset-y-0 end-0 w-96 glass` drawer with its own `bg-black/20`
 * scrim, toggled by a `showAuditLog` boolean and living outside every layout
 * the page had. Who is on the task was three avatar bubbles in the header,
 * and reading an attachment was a 60%-wide slide-over that covered the list
 * you opened it from.
 *
 * All four are PANES on the shared `DetailPageSurface` now - they dock left
 * or right, stack, reorder by dragging their header, resize, and are
 * remembered per user. See `surface/layout` for the pane set and for why this
 * one surface is GLOBAL-scoped where matters and projects are per-record.
 *
 * THE LAYERING, outside in:
 *
 *   DetailFrame        breadcrumbs, the primary action, the overflow menu
 *     DetailTabBar     the canvas's tabs + the pane rail, full width
 *     DetailPageSurface  the canvas, with the panes over its edges
 *
 * The tab bar is a SIBLING of the surface, not its first child: navigation is
 * the frame around the work, so a docked pane must not sit over it or shrink
 * it.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { RootState, useAppDispatch } from '../../store';
import { useSelector } from 'react-redux';
import { fetchTaskDetails, updateTask, completeTask, addTaskComment, uploadTaskAttachment, deleteTask, archiveTask, removeTaskAttachment, addTaskCollaborator, removeTaskCollaborator, updateCollaboratorPrivilege, listTaskComments, listTaskAttachments, listTaskCollaborators, listTaskRelatedItems, removeTaskRelatedItem, fetchTaskFeed, postTaskFeedEvent, addTaskFeedComment } from '../../store/actions/TasksActions';
import { useGlobalStreaming } from '../../hooks/useGlobalStreaming';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from '../shared/TailwindComponents/Button';
import Dropdown, { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { getUserType } from '../../store/actions/ChatAction';
import dayjs from 'dayjs';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { showSidePanel, hideSidePanel } from '../../store/actions/sidePanelActions';
import { useTranslation } from 'react-i18next';
import { PiCheckLight, PiTrashLight, PiClockClockwiseLight, PiClockCounterClockwiseLight, PiFileTextLight, PiCloudArrowDownLight, PiArchiveLight, PiCheckCircleLight, PiChatCircleTextLight, PiUsersLight, PiPaperclipLight, PiCalendarBlankLight, PiFlagLight, PiInfoLight, PiProjectorScreenChartLight, PiSuitcaseSimpleLight } from "react-icons/pi";
import ProfileImage from '../Header/ProfileImage';
import Badge from '../shared/TailwindComponents/Badge';
import { formatDate, formatDateTime, formatStatus, formatBytes, parseServerDate } from '../../utils/functions';
import { fetchMatters } from '../../store/actions/Matters/MatterActions';
import DetailFrame from '../shared/Frames/DetailFrame';
import TaskDetailsContent from './TaskDetailsContent';
import DetailTabBar, { TabItem } from '../shared/surface/DetailTabBar';
import DetailPageSurface from '../shared/surface/DetailPageSurface';
import PaneRail from '../shared/surface/PaneRail';
import PaneBody from '../shared/surface/PaneBody';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, taskDetailTourSteps } from '../shared/FeatureTour';
import { fetchAllEmployees } from '../../store/actions/organization/employeeActions';
import ProjectBadges from '../Projects/ProjectBadges';
import PendingAccessRequests from '../Projects/PendingAccessRequests';
import { TASK_SURFACE, TASK_TABS, useTaskWorkspace } from './surface';
import { TaskSurfaceProvider } from './surface/TaskSurfaceContext';
import TaskContextPane from './surface/TaskContextPane';
import AccessPane from '../shared/access/AccessPane';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import { taskPriorityColor, taskStatusColor } from './taskFacts';
import { normalise } from '../shared/access/accessModel';
import { toAttachment } from '../shared/attachments';
import { toFeedActivityEntries } from '../shared/activity/activityModel';
import { toErrorText } from '../../utils/errorMessage';

interface TaskDetailParams {
  taskUuid: string;
}

const normalizeAttachment = (attachment: any, taskUuid?: string): any => {
  const attachmentId = attachment.attachment_id || attachment.AttachmentID || attachment.id;
  const fileUrl = attachment.FileURL ||
    (taskUuid && attachmentId ? `/tasks/task/${taskUuid}/attachments/${attachmentId}/download` : attachment.blob_path);

  const filename = attachment.filename || attachment.FileName || '';
  const fileExtension = filename.includes('.') ? filename.split('.').pop() || 'file' : 'file';

  const contentType = attachment.content_type || attachment.FileType || '';
  const fileType = contentType || fileExtension;

  return {
    AttachmentID: attachmentId,
    id: attachmentId,
    FileName: filename,
    FileType: fileType,
    ContentType: contentType,
    FileURL: fileUrl,
    blobPath: attachment.blob_path,
    size: attachment.size,
    uploadedAt: attachment.uploaded_at || attachment.CreationDate,
    uploadedBy: attachment.uploaded_by_user || null,
  };
};

const normalizeComment = (comment: any, usersMap?: Record<number, any>): any | null => {
  if (!comment || !comment.comment_text) {
    return null;
  }

  const createdByObject = comment.created_by || {};
  const createdByUserId = comment.created_by_user_id || createdByObject.id;

  const firstName = createdByObject.first_name || '';
  const lastName = createdByObject.last_name || '';
  const email = createdByObject.email || 'unknown@example.com';

  const initials = `${(firstName || '').charAt(0).toUpperCase()}${(lastName || '').charAt(0).toUpperCase()}`.trim() || 'U';
  const fullName = `${firstName} ${lastName}`.trim() || 'Unknown';

  return {
    uuid: comment.uuid,
    CommentText: comment.comment_text || comment.CommentText,
    UserName: fullName,
    UserEmail: email,
    Timestamp: comment.created_at || comment.Timestamp,
    ProfilePicture: createdByObject.profile_picture || null,
    Initials: initials,
    is_pinned: comment.is_pinned,
    is_edited: comment.is_edited,
    created_by_user_id: createdByUserId,
  };
};

const normalizeCollaborator = (collaborator: any, usersMap?: Record<number, any>): any => {
  const name = collaborator.name || '';
  const email = collaborator.email || 'unknown@example.com';
  const userId = collaborator.collaborator_id || collaborator.id || collaborator.user_id;

  let initials = collaborator.initials;
  if (!initials && name) {
    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      initials = `${nameParts[0].charAt(0).toUpperCase()}${nameParts[nameParts.length - 1].charAt(0).toUpperCase()}`;
    } else {
      initials = nameParts[0]?.charAt(0).toUpperCase() || 'U';
    }
  }
  initials = initials || 'U';

  const firstName = collaborator.first_name || '';
  const lastName = collaborator.last_name || '';
  const legacyInitials = `${(firstName || '').charAt(0).toUpperCase()}${(lastName || '').charAt(0).toUpperCase()}`.trim() || 'U';
  const legacyFullName = `${firstName} ${lastName}`.trim() || 'Unknown';

  const fullName = name || legacyFullName;
  const displayInitials = name ? initials : legacyInitials;

  const access = collaborator.access || (collaborator.role === 'editor' || collaborator.role === 'admin' ? 'edit' : 'view');
  const role = collaborator.role || 'collaborator';

  const addedByObj = collaborator.added_by || {};
  const addedByName = addedByObj.name || `${addedByObj.first_name || ''} ${addedByObj.last_name || ''}`.trim() || 'Unknown';
  const addedByEmail = addedByObj.email || '';
  const addedByUserId = collaborator.added_by_user_id || addedByObj.id;

  let addedByInitials = addedByObj.initials;
  if (!addedByInitials && addedByName) {
    const nameParts = addedByName.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      addedByInitials = `${nameParts[0].charAt(0).toUpperCase()}${nameParts[nameParts.length - 1].charAt(0).toUpperCase()}`;
    } else {
      addedByInitials = nameParts[0]?.charAt(0).toUpperCase() || 'U';
    }
  }
  addedByInitials = addedByInitials || 'U';

  return {
    uuid: collaborator.collaborator_id || collaborator.id || collaborator.uuid,
    UserID: userId,
    Name: fullName,
    Email: email,
    ProfilePicture: collaborator.avatar_url || collaborator.user?.profile_picture || collaborator.profile_picture || null,
    Initials: displayInitials,
    access: access,
    role: role,
    CanCompleteOrArchive: access === 'edit' || access === 'admin' || role === 'editor' || role === 'admin',
    created_at: collaborator.created_at || collaborator.shared_at,
    shared_by: collaborator.shared_by,
    added_by: addedByEmail || addedByUserId ? {
      Name: addedByName,
      Email: addedByEmail,
      FirstName: addedByName.split(' ')[0] || '',
      LastName: addedByName.split(' ')[1] || '',
      Initials: addedByInitials,
      UserID: addedByUserId,
    } : null,
  };
};

const EMPTY_PEOPLE: any[] = [];

const TaskDetail: React.FC = () => {
  const { t } = useTranslation();
  const { taskUuid } = useParams<TaskDetailParams>();
  const dispatch = useAppDispatch();
  const userType = getUserType();
  const { setBreadcrumbs } = useBreadcrumbs();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const history = useHistory();
  const location = useLocation();

  const [isEditing, setIsEditing] = useState(false);
  const [userAccessLevel, setUserAccessLevel] = useState<'edit' | 'view' | 'owner' | 'admin'>('owner');
  const [taskDetails, setTaskDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [collaborators, setCollaborators] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [matterData, setMatterData] = useState<any>(null);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [collaboratorsLoading, setCollaboratorsLoading] = useState(false);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [matterLoading, setMatterLoading] = useState(false);
  const [newComment, setNewComment] = useState<string>('');
  const [newCollaboratorEmail, setNewCollaboratorEmail] = useState<string>('');
  const [selectedCaseId, setSelectedCaseId] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isNewTask, setIsNewTask] = useState(false);
  const [taskFeed, setTaskFeed] = useState<any[]>([]);
  const [taskFeedLoading, setTaskFeedLoading] = useState(false);
  const employees = useSelector((state: RootState) => state.employees.employees);
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);

  const [matters, setMatters] = useState<any[]>([]);
  const [mattersLoading, setMattersLoading] = useState(false);
  const people = useSelector((state: RootState) => state.orgPeople?.people ?? EMPTY_PEOPLE);
  const [newCollaboratorAccess, setNewCollaboratorAccess] = useState<'edit' | 'view'>('view');
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set([]));
  /* How many projects the header badges found - the overflow menu greys out
     "Link to project" off this, and the badges are the only thing that asks. */
  const [linkedProjectCount, setLinkedProjectCount] = useState(0);
  /* Bumped after linking from the MENU, to remount the badges so they refetch
     - the panel opened from the menu has no handle on their loader. */
  const [projectBadgeNonce, setProjectBadgeNonce] = useState(0);

  /**
   * The workspace: pane layout, the reader's content and the active tab, from
   * the ONE shared hook. Keyed by user only - the surface is global-scoped,
   * so all four hundred task pages share one arrangement (see
   * `surface/layout`).
   */
  const {
    panels,
    api: surfaceApi,
    activeTab,
    goToTab,
  } = useTaskWorkspace({
    // The raw query param: the hook checks it against the surface's declared
    // tabs and falls back to the first, so a stale `?tab=` from an older link
    // cannot leave the canvas blank.
    initialTab: new URLSearchParams(location.search).get('tab') || undefined,
    onTabChange: (tab) => history.push(`/tasks/${taskUuid}?tab=${tab}`),
  });

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'task-detail',
    steps: taskDetailTourSteps,
    readyToShow: !loading && !!taskDetails,
    autoStartDelay: 600,
  });

  const usersMap = useMemo(() => {
    const map: Record<number, any> = {};
    employees.forEach((emp: any) => {
      if (emp.id || emp.UserID) {
        map[emp.id || emp.UserID] = emp;
      }
    });
    return map;
  }, [employees]);

  const employeeOptions: DropdownOption[] = useMemo(() => people.map((person) => ({
    value: person.email,
    label: `${person.first_name} ${person.last_name}`,
  })), [people]);

  const renderCollaboratorOption = useCallback((option: DropdownOption): React.ReactNode => {
    const person = people.find(p => p.email === option.value);
    if (!person) {
      return <span className="text-xs">{option.label}</span>;
    }

    const initials = `${(person.first_name || '').charAt(0).toUpperCase()}${(person.last_name || '').charAt(0).toUpperCase()}`.trim() || 'U';

    return (
      <div className="flex items-center gap-3 w-full">
        <ProfileImage
          profilePicture={normalise(person).avatar}
          email={person.email || null}
          initials={initials}
          firstName={person.first_name}
          lastName={person.last_name}
          size="h-8 w-8"
          className="flex-shrink-0"
        />
        <span className="text-xs truncate">{person.first_name} {person.last_name}</span>
      </div>
    );
  }, [people]);

  // Fetch matters and people on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setMattersLoading(true);
        const mattersResponse = await dispatch(fetchMatters({
          page: 1,
          page_size: 100,
        }) as any);
        if (mattersResponse && mattersResponse.matters) {
          setMatters(mattersResponse.matters);
        }

        await dispatch(fetchOrgPeople() as any);
      } catch (error) {
      } finally {
        setMattersLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  // Fetch task details when taskUuid changes
  useEffect(() => {
    if (!taskUuid) return;

    let isMounted = true;
    setLoading(true);
    setError(null);

    dispatch(fetchTaskDetails(taskUuid) as any)
      .then((response: any) => {
        if (!isMounted) return;

        if (response && (response.id || response.uuid)) {
          setTaskDetails(response);
          // Handle both response formats (uppercase and lowercase)
          const collaboratorsData = response.Collaborators || response.collaborators || [];
          const commentsData = response.Comments || response.comments || [];
          const attachmentsData = response.Attachments || response.attachments || [];

          const normalizedComments = commentsData
            .map((comment: any) => normalizeComment(comment, usersMap))
            .filter((comment: any) => comment !== null);
          setComments(normalizedComments);

          const normalizedCollaborators = collaboratorsData.map((collab: any) =>
            normalizeCollaborator(collab, usersMap)
          );
          setCollaborators(normalizedCollaborators);

          const normalizedAttachments = attachmentsData.map((att: any) => normalizeAttachment(att, taskUuid));
          setAttachments(normalizedAttachments);

          const relatedMatters = response.related_items || [];
          setMatterData(relatedMatters.length > 0 ? relatedMatters[0] : null);

          const currentUserAsCreator = response.assigned_to_user_id === userInfo?.UserID || response.created_by_user_id === userInfo?.UserID;
          if (currentUserAsCreator) {
            setUserAccessLevel('owner');
          } else {
            const userCollaborator = collaboratorsData.find((collab: any) => {
              const collabUserId = collab.collaborator_user_id || collab.user?.id || collab.user_id;
              if (collabUserId === userInfo?.UserID) {
                return true;
              }
              if (collab.email === userInfo?.Email) {
                return true;
              }
              return false;
            });
            if (userCollaborator) {
              let access: 'edit' | 'view' | 'admin' = 'view';
              if (userCollaborator.access) {
                if (userCollaborator.access === 'admin') {
                  access = 'admin';
                } else if (userCollaborator.access === 'edit') {
                  access = 'edit';
                } else {
                  access = 'view';
                }
              } else if (userCollaborator.role === 'viewer') {
                access = 'view';
              } else if (userCollaborator.role === 'admin' || userCollaborator.role === 'creator') {
                access = 'edit';
              }
              setUserAccessLevel(access as any);
            }
          }
        } else {
          setAccessDenied(true);
          setTaskDetails(null);
        }
        setLoading(false);
      })
      .catch((err: any) => {
        if (!isMounted) return;
        setAccessDenied(true);
        setTaskDetails(null);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [taskUuid, dispatch]);

  const refetchTabData = useCallback(async (tab: string) => {
    if (!taskUuid) return;

    switch (tab) {
      case 'Feed':
        await loadTaskFeed();
        break;

      case 'Attachments':
        setAttachmentsLoading(true);
        try {
          const attachmentsData = await dispatch(listTaskAttachments(taskUuid) as any);
          if (attachmentsData?.attachments) {
            const normalizedAttachments = (attachmentsData.attachments || []).map((att: any) => normalizeAttachment(att, taskUuid));
            setAttachments(normalizedAttachments);
          }
        } catch (error) {
          console.error('Error fetching attachments:', error);
          // Preserve existing attachments on error
        } finally {
          setAttachmentsLoading(false);
        }
        break;

      case 'Details':
        setCollaboratorsLoading(true);
        try {
          const collaboratorsData = await dispatch(listTaskCollaborators(taskUuid) as any);
          if (collaboratorsData?.collaborators) {
            const normalizedCollaborators = (collaboratorsData.collaborators || []).map((collab: any) =>
              normalizeCollaborator(collab, usersMap)
            );
            setCollaborators(normalizedCollaborators);
          }
        } catch (error) {
          console.error('Error fetching collaborators:', error);
          // Preserve existing collaborators on error
        } finally {
          setCollaboratorsLoading(false);
        }
        break;
    }
  }, [taskUuid, dispatch, usersMap]);

  // Fetch tab-specific data on first tab open (not on every tab change)
  useEffect(() => {
    if (!taskUuid || !activeTab || loadedTabs.has(activeTab)) return;

    const fetchTabData = async () => {
      try {
        if (activeTab === 'Feed' && taskFeed.length === 0) {
          await loadTaskFeed();
        } else if (activeTab === 'Attachments' && attachments.length === 0) {
          setAttachmentsLoading(true);
          const attachmentsData = await dispatch(listTaskAttachments(taskUuid) as any);
          if (attachmentsData?.attachments) {
            const normalizedAttachments = (attachmentsData.attachments || []).map((att: any) => normalizeAttachment(att, taskUuid));
            setAttachments(normalizedAttachments);
          }
          setAttachmentsLoading(false);
        }
      } catch (error) {
        console.error(`Error fetching ${activeTab} data:`, error);
      }

      // Mark tab as loaded
      setLoadedTabs(prev => new Set(prev).add(activeTab));
    };

    fetchTabData();
  }, [activeTab, taskUuid, dispatch]);

  const matterOptions: DropdownOption[] = useMemo(() => matters.map((matter: any) => ({
    value: matter.uuid || matter.id || matter.MatterID,
    label: matter.title || matter.MatterName || `${t('Matter')} ${matter.id || matter.MatterID}`,
  })), [matters, t]);

  const priorityOptions: DropdownOption[] = useMemo(() => [
    { value: 'low', label: t('Low') },
    { value: 'medium', label: t('Medium') },
    { value: 'high', label: t('High') },
  ], [t]);

  useEffect(() => {
    if (taskDetails) {
      setBreadcrumbs([
        { name: t('Tasks'), href: '/tasks', current: false },
        { name: taskDetails.title || t('Task Details'), href: `/tasks/${taskUuid}`, current: true },
      ]);
    } else {
      setBreadcrumbs([
        { name: t('Tasks'), href: '/tasks', current: false },
        { name: t('Loading...'), href: `/tasks/${taskUuid}`, current: true },
      ]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskDetails?.title, taskUuid, setBreadcrumbs]);

  const refetchTaskDetails = useCallback(() => {
    if (!taskUuid) return;

    setLoading(true);
    setError(null);

    dispatch(fetchTaskDetails(taskUuid) as any)
      .then((response: any) => {
        if (response && (response.id || response.uuid)) {
          setTaskDetails(response);
          const normalizedComments = (response.Comments || [])
            .map((comment: any) => normalizeComment(comment, usersMap))
            .filter((comment: any) => comment !== null);
          setComments(normalizedComments);
          
          // Only update collaborators if we have new data, otherwise preserve existing
          if (response.Collaborators && response.Collaborators.length > 0) {
            const normalizedCollaborators = (response.Collaborators || []).map((collab: any) =>
              normalizeCollaborator(collab, usersMap)
            );
            setCollaborators(normalizedCollaborators);
          }
          
          const normalizedAttachments = (response.Attachments || []).map((att: any) => normalizeAttachment(att, taskUuid));
          setAttachments(normalizedAttachments);
          const relatedMatters = response.related_items || [];
          setMatterData(relatedMatters.length > 0 ? relatedMatters[0] : null);
        } else {
          setError('Task not found');
          setTaskDetails(null);
        }
        setLoading(false);
      })
      .catch((err: any) => {
        console.error('Error loading task details:', err);
        setError('Error loading task');
        // Preserve existing task details on error instead of clearing
        setLoading(false);
      });
  }, [taskUuid, dispatch, usersMap]);

  const validationSchema = useMemo(() =>
    Yup.object({
      title: Yup.string().required(t('titleRequired')),
      description: Yup.string().required(t('descriptionRequired')),
      due_date: Yup.string().nullable().notRequired(),
      priority: Yup.string().oneOf(['low', 'medium', 'high'], t('invalidPriority')).required(t('priorityRequired')),
    }),
  [t]);

  const onSubmitHandler = useCallback(async (values: any) => {
    try {
      const result = await dispatch(
        updateTask(taskUuid, {
          title: values.title,
          description: values.description,
          due_date: values.due_date,
          priority: values.priority,
        })
      );
      if (!result) return; // Action failed - handleApiError already showed the error toast
      dispatch(
        showFeedbackModal({
          modalType: 'success',
          message: t('Task updated successfully'),
          showModal: true,
          duration: 3000,
        })
      );
      setIsEditing(false);
      setHasChanges(false);
      // Refetch to get latest data, but preserve collaborators
      refetchTaskDetails();
    } catch (error: any) {
      console.error(t('Error updating task. Please try again later.'), error);
      dispatch(
        showFeedbackModal({
          modalType: 'error',
          message: t('Error updating task. Please try again later.'),
          showModal: true,
          duration: 3000,
        })
      );
    }
  }, [taskUuid, dispatch, t, refetchTaskDetails]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: taskDetails?.title || '',
      description: taskDetails?.description || '',
      due_date: parseServerDate(taskDetails?.due_date)?.toISOString() || '',
      priority: taskDetails?.priority || 'medium',
    },
    validationSchema,
    onSubmit: onSubmitHandler,
  });

  const handleChangeMatter = useCallback(async () => {
    if (!selectedCaseId) {
      dispatch(showFeedbackModal({ showModal: true, message: t('Error selecting matter. Please try again later.'), modalType: 'error', duration: 3000 }));
      return;
    }
    setMatterLoading(true);
    try {
      // Update the task's matter_uuid directly via the update endpoint
      const result = await dispatch(updateTask(taskUuid, { matter_uuid: selectedCaseId }) as any);
      if (!result) return; // Action failed - handleApiError already showed the error toast
      setSelectedCaseId('');
      // Reload full task details to refresh matter information
      await refetchTaskDetails();
      dispatch(showFeedbackModal({ showModal: true, message: t('Matter updated successfully'), modalType: 'success', duration: 3000 }));
    } catch (error: any) {
      console.error(t('Error updating matter. Please try again later.'), error);
    } finally {
      setMatterLoading(false);
    }
  }, [selectedCaseId, dispatch, taskUuid, t, refetchTaskDetails]);

  const handleComplete = useCallback(async () => {
    try {
      dispatch(
        showModal({
          type: 'addTaskComment',
          message: t('Complete Task'),
          icon: <PiCheckCircleLight className='w-6 h-6 text-success-600' />,
          subMsg: t('Add an optional comment to let others know about the completion.'),
          onConfirm: async (comment: string) => {
            setCommentsLoading(true);
            try {
              await dispatch(completeTask(taskUuid) as any);
              if (comment.trim()) {
                await dispatch(addTaskComment(taskUuid, comment) as any);
              }
              await refetchTaskDetails();
              dispatch(hideModal());
            } catch (error: any) {
              dispatch(showFeedbackModal({
                modalType: 'error',
                message: t('Error completing task. Please try again later.'),
                showModal: true,
                duration: 3000,
              }));
            } finally {
              setCommentsLoading(false);
            }
          },
          showModal: true,
          onCancel: () => dispatch(hideModal()),
          duration: 3000,
        })
      );
    } catch (error) {
      console.error(t('Error completing task. Please try again later.'), error);
      setCommentsLoading(false);
    }
  }, [dispatch, taskUuid, t, refetchTaskDetails]);

  const handleArchive = useCallback(async () => {
    try {
      dispatch(
        showModal({
          showModal: true,
          type: 'confirmation',
          onConfirm: async () => {
            dispatch(archiveTask(taskUuid) as any);
            refetchTaskDetails();
            dispatch(hideModal());
          },
          message: t('Archive Task'),
          subMsg: t('This task will be archived. You can restore it later if needed.'),
          icon: <PiArchiveLight className='w-6 h-6 text-danger-600' />,
        })
      );
    } catch (error) {
      console.error(t('Error archiving task. Please try again later.'), error);
    }
  }, [dispatch, taskUuid, t, refetchTaskDetails]);

  const handleRestore = useCallback(async () => {
    try {
      const result = await dispatch(updateTask({ TaskID: taskUuid, Status: 'todo' }) as any);
      if (!result) return; // Action failed - handleApiError already showed the error toast
      dispatch(showFeedbackModal({ modalType: 'success', message: t('Task restored.'), showModal: true, duration: 3000 }));
      refetchTaskDetails();
    } catch (error) {
      console.error(t('Error restoring task. Please try again later.'), error);
    }
  }, [dispatch, taskUuid, t, refetchTaskDetails]);

  const handleAddComment = useCallback(async () => {
    if (!newComment.trim()) return;
    const tempComment = {
      uuid: `temp-${Date.now()}`,
      CommentText: newComment,
      UserName: `${userInfo?.FirstName || ''} ${userInfo?.LastName || ''}`.trim(),
      UserEmail: userInfo?.Email,
      Initials: `${(userInfo?.FirstName || '').charAt(0).toUpperCase()}${(userInfo?.LastName || '').charAt(0).toUpperCase()}`.trim() || 'U',
      ProfilePicture: userInfo?.ProfilePicture,
      Timestamp: dayjs().toISOString(),
    };
    setComments(prev => [...prev, tempComment]);
    setNewComment('');
    try {
      await dispatch(addTaskComment(taskUuid, newComment) as any);
    } catch (error: any) {
      setComments(prev => prev.filter(c => c.uuid !== tempComment.uuid));
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Error adding comment. Please try again later.'),
        showModal: true,
        duration: 3000,
      }));
    }
  }, [newComment, userInfo, dispatch, taskUuid, t]);

  // ─── Task Feed (SQL-backed) handlers ───────────────────────────────────
  const loadTaskFeed = useCallback(async () => {
    setTaskFeedLoading(true);
    try {
      const result = await dispatch(fetchTaskFeed(taskUuid) as any);
      if (Array.isArray(result)) {
        setTaskFeed(result);
      }
    } finally {
      setTaskFeedLoading(false);
    }
  }, [dispatch, taskUuid]);

  /**
   * The feed, as activity entries.
   *
   * See the Activity pane below: the audit trail does not know about feed
   * posts, so the merge happens here rather than nowhere.
   */
  const feedActivityEntries = useMemo(() => toFeedActivityEntries(taskFeed), [taskFeed]);

  /* The Activity pane can be raised without ever opening the Feed tab, and it
     must not then show a task with no posts on it. Loads once, on first open. */
  const activityPaneOpen = panels.layout.slots['activity']?.visible ?? false;
  useEffect(() => {
    if (!activityPaneOpen || loadedTabs.has('Feed') || taskFeed.length > 0 || taskFeedLoading) return;
    loadTaskFeed();
    setLoadedTabs((prev) => new Set(prev).add('Feed'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityPaneOpen]);

  const handlePostTaskFeedEvent = useCallback(async (eventData: { event_type: string; message: string; metadata?: any }) => {
    await dispatch(postTaskFeedEvent(taskUuid, eventData) as any);
    await loadTaskFeed();
  }, [dispatch, taskUuid, loadTaskFeed]);

  const handleAddTaskFeedComment = useCallback(async (eventId: string, comment: string) => {
    await dispatch(addTaskFeedComment(taskUuid, eventId, comment) as any);
    await loadTaskFeed();
  }, [dispatch, taskUuid, loadTaskFeed]);

  const handleFileUpload = useCallback(async (files: File[]) => {
    setAttachmentsLoading(true);
    try {
      for (const file of files) {
        await dispatch(uploadTaskAttachment(taskUuid, file) as any);
      }
      await refetchTabData('Attachments');
    } catch (error: any) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Error uploading file. Please try again later.'),
        showModal: true,
        duration: 3000,
      }));
    } finally {
      setAttachmentsLoading(false);
    }
  }, [dispatch, taskUuid, t, refetchTabData]);

  const handleOpenUploadModal = useCallback(() => {
    dispatch(
      showSidePanel({
        type: 'uploadAttachment',
        showSidePanel: true,
        size: 'sm',
        message: t('Upload Attachments'),
        icon: <PiCloudArrowDownLight className='w-5 h-5 text-gray-600' />,
        onConfirm: async (data: any) => {
          if (data?.files?.length) {
            await handleFileUpload(data.files);
          }
        },
        onCancel: () => dispatch(hideSidePanel()),
      } as any)
    );
  }, [dispatch, t, handleFileUpload]);

  const { viewFile } = useGlobalStreaming();

  /**
   * Open an attachment in the GLOBAL SIDE PANEL.
   *
   * It was a pane of this page's own surface, docked beside the Attachments
   * table. Reading a document is the app's slide-in drawer everywhere else —
   * drafts, files, research sources - and a document that opens as a drawer on
   * one page and as an embedded column on another is two answers to one
   * question. So it is the drawer here too.
   *
   * The BODY is unchanged: `TaskReaderPane`, the same Document info block over
   * the same `ReaderComponent`, and the same `useGlobalStreaming` blob fetch.
   * Only its host moved.
   *
   * IT OPENS BEFORE THE BLOB ARRIVES. The fetch takes seconds, so the drawer
   * appears immediately with the filename and a loader - the wait happens
   * somewhere visible rather than behind a row press that looks dead. The
   * second dispatch replaces that state with the document; dispatching over
   * the panel is also what makes a SECOND attachment swap the drawer's
   * contents instead of stacking another one.
   */
  const handleOpenAttachment = useCallback(async (attachment: any) => {
    const fileType = (attachment.FileType || '').toLowerCase();
    const attachmentId = attachment.AttachmentID || attachment.id;
    const filename = attachment.FileName;
    // 'owner' is the page's word for it; the reader only knows three levels.
    const readerAccess = userAccessLevel === 'owner' ? 'admin' : userAccessLevel;
    /* Carried into the panel so its Document info block can answer size, type,
       uploader and date without a second request. */
    const normalizedAttachment = toAttachment(attachment);

    /* The reader drawer's shape, as every other reader in the app dispatches
       it: 65% wide, filename on the header line, "Document Preview" under it.
       See `AIStudioIndexDetailPage`. */
    const openPanel = (data: any) => dispatch(showSidePanel({
      type: 'taskAttachmentReader',
      showSidePanel: true,
      width: '65%',
      message: filename,
      subMsg: t('Document Preview'),
      data,
    }));

    openPanel({
      title: filename,
      loading: true,
      taskUuid,
      userAccessLevel: readerAccess,
      attachment: normalizedAttachment,
    });

    try {
      const result = await viewFile({
        domain: 'tasks',
        resourceId: taskUuid,
        fileId: attachmentId,
        filename: filename,
        contentType: fileType,
      });

      if (!result.success || !result.blob) {
        dispatch(hideSidePanel());
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: toErrorText(result.error) || t('Failed to load attachment'),
          showModal: true,
          duration: 3000,
        }));
        return;
      }

      openPanel({
        title: filename,
        taskUuid,
        userAccessLevel: readerAccess,
        attachment: normalizedAttachment,
        citation: {
          title: filename,
          name: filename,
          blob: result.blob,
          blobObject: result.blob,
          contentType: result.contentType || fileType,
          type: 'file',
          id: attachmentId,
        },
      });
    } catch (error: any) {
      dispatch(hideSidePanel());
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Error opening attachment. Please try again.'),
        showModal: true,
        duration: 3000,
      }));
    }
  }, [dispatch, taskUuid, viewFile, t, userAccessLevel]);

  /*
    NO PER-ROW REMOVE ON THIS PAGE.

    `handleDeleteCollaborator` lived here, was threaded through
    `TaskDetailsContent` (which never rendered a control for it) and was
    revived as a hover-reveal trash can in the task's access pane - the only
    page in the app with an immediate, unstaged write beside a read-only
    list. Removal happens in the shared `manageResourceCollaborators` panel,
    which this page opens with `canAdmin: true` (see
    `handleManageCollaborators` below), so the trash can there is available to
    the same people the pane's was - including non-admin editors.
  */
  const handleDeleteAttachment = useCallback((attachmentId: string) => {
    dispatch(showModal({
      type: 'confirmation',
      onConfirm: async () => {
        try {
          await dispatch(removeTaskAttachment(taskUuid, attachmentId) as any);
          await refetchTabData('Attachments');
          dispatch(hideModal());
        } catch (error: any) {
          dispatch(showFeedbackModal({
            modalType: 'error',
            message: t('Error deleting attachment. Please try again later.'),
            showModal: true,
            duration: 3000,
          }));
          dispatch(hideModal());
        }
      },
      showModal: true,
      message: t('Remove Attachment'),
      icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
      subMsg: t('This attachment will be permanently removed from the task. This action cannot be undone.'),
      isDest: true
    }))
  }, [dispatch, taskUuid, t, refetchTabData]);

  const taskStatus = (taskDetails?.status || 'todo').toLowerCase();

  const availableEmployees = employeeOptions.filter(
    (emp) =>
      !collaborators.some((collab: any) => collab.Email === emp.value) &&
      emp.value !== userInfo?.Email
  );

  const clientOption: any[] = [];
  const collaboratorOptions = [...availableEmployees, ...clientOption];

  /* The VALUES come from the surface config - one vocabulary for the bar, the
     URL and `goToTab`. Only the labels and the icons are the page's. */
  const tabs: TabItem[] = useMemo(() => {
    const chrome: Record<typeof TASK_TABS[number], { label: string; icon: React.ReactNode }> = {
      Details: { label: t('Details'), icon: <PiFileTextLight className="h-4 w-4" /> },
      Feed: { label: t('Feed'), icon: <PiChatCircleTextLight className="h-4 w-4" /> },
      Attachments: { label: t('Attachments'), icon: <PiPaperclipLight className="h-4 w-4" /> },
    };
    return TASK_TABS.map((value) => ({ value, ...chrome[value] }));
  }, [t]);

  const taskBreadcrumbs = useMemo(
    () => [
      { name: t('Tasks'), href: '/tasks', current: false },
      {
        name: taskDetails?.title || t('Task Details'),
        href: `/tasks/${taskUuid}`,
        current: true,
      },
    ],
    [taskDetails?.title, taskUuid, t]
  );

  const taskActions = useMemo(() => {
    if (loading || !taskDetails) return [];
    const actions: { label: string; onClick: () => void; icon?: React.ReactElement }[] = [];
    if (taskStatus === 'todo' && userAccessLevel !== 'view') {
      actions.push({ label: t('Complete'), onClick: handleComplete, icon: <PiCheckLight className="w-4 h-4" /> });
    }
    if (taskStatus !== 'todo') {
      actions.push({ label: t('Restore'), onClick: handleRestore, icon: <PiClockClockwiseLight /> });
    }
    return actions;
  }, [loading, taskDetails, taskStatus, userAccessLevel, handleComplete, handleRestore, t]);

  /**
   * The shared manage-access side panel.
   *
   * STAYS A SIDE PANEL. It is a wide, transactional editor - add someone,
   * change a level, remove - used for a minute and dismissed, and it is the
   * same panel matters and projects open. What became a PANE is the reading
   * half: the list of who is on this task (the shared `AccessPane`), which is
   * exactly the part someone wants next to the work rather than over it.
   *
   * Lifted out of the header menu so the pane's own button opens the same
   * panel rather than a second copy of this payload.
   */
  const handleManageCollaborators = useCallback(() => {
    const panelCollaborators = collaborators.map((c: any) => ({
      user_id: c.UserID || c.user_id,
      collaborator_uuid: c.uuid || c.collaborator_uuid,
      name: c.Name || c.name || '',
      email: c.Email || c.email || '',
      initials: c.Initials || c.initials || '',
      profile_picture: c.ProfilePicture || c.profile_picture || null,
      access: c.access || 'view',
      role: c.role,
      shared_at: c.created_at || c.shared_at,
      shared_by: c.shared_by,
      shared_by_user: c.added_by ? {
        name: c.added_by.Name || c.added_by.name,
        full_name: c.added_by.Name || c.added_by.name,
        email: c.added_by.Email || c.added_by.email,
      } : c.shared_by_user,
      source: 'direct',
    }));
    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      size: 'md',
      message: t('Manage Access'),
      resourceUuid: taskUuid,
      resourceType: 'task',
      collaborators: panelCollaborators,
      currentUserUuid: userInfo?.UserUID,
      /* Was hardcoded `true`, which handed the add/remove/level controls to
         anyone who opened the panel - including a view-only collaborator
         reaching it through "View access". */
      canAdmin: userAccessLevel !== 'view',
      addCollaborator: async (uuid: string, userUuid: string, access: string) => {
        await dispatch(addTaskCollaborator(uuid, userUuid, undefined, access as any) as any);
      },
      removeCollaborator: async (uuid: string, collabUuid: string) => {
        await dispatch(removeTaskCollaborator(uuid, collabUuid) as any);
      },
      updateAccess: async (uuid: string, collabUuid: string, access: string) => {
        await dispatch(updateCollaboratorPrivilege(uuid, collabUuid, access) as any);
      },
      onUpdate: async () => {
        await refetchTabData('Details');
      },
    }));
  }, [dispatch, t, taskUuid, collaborators, userInfo?.UserUID, refetchTabData, userAccessLevel]);

  /** The project picker - the same panel the header badges open. */
  const handleLinkToProject = useCallback(() => {
    dispatch(showSidePanel({
      type: 'connectToProject',
      showSidePanel: true,
      size: 'sm',
      resourceType: 'task',
      resourceUuid: taskUuid,
      message: t('Link to project'),
      subMsg: t('Link this task to one or more projects.'),
      icon: <PiProjectorScreenChartLight className="w-6 h-6 text-fg-muted" />,
      onChanged: () => setProjectBadgeNonce((n) => n + 1),
      onCancel: () => dispatch(hideSidePanel()),
    }));
  }, [dispatch, t, taskUuid]);

  /** The matter picker - replaces the Dropdown buried in the Details tab. */
  const handleLinkToMatter = useCallback(() => {
    dispatch(showSidePanel({
      type: 'linkTaskToMatter',
      showSidePanel: true,
      size: 'sm',
      taskUuid,
      currentMatter: taskDetails?.matter || null,
      message: t('Link to matter'),
      subMsg: t('Put this task on a matter.'),
      icon: <PiSuitcaseSimpleLight className="w-6 h-6 text-fg-muted" />,
      onChanged: () => refetchTaskDetails(),
      onCancel: () => dispatch(hideSidePanel()),
    }));
  }, [dispatch, t, taskUuid, taskDetails?.matter, refetchTaskDetails]);

  /**
   * The overflow menu: LINKING and archive.
   *
   * "Activity Log" is gone - the pane rail raises Activity with one press and
   * a menu entry duplicating a visible toggle is a menu entry nobody needs.
   * In its place are the two links a task can have, each DISABLED (with the
   * reason as its title) when the link already exists, so the menu states the
   * task's relationships rather than only offering to change them. Archive
   * stays, so it is never alone in here.
   */
  const headerDropdownActions = useMemo(() => {
    const actions: { label: string; onClick: () => void; icon: React.ReactElement; disabled?: boolean; title?: string; variant?: 'neutral' | 'outline' | 'destructive' | 'primary' }[] = [];

    /* NO "Manage Access" HERE - the rail's Access pill raises the pane that
       lists who is on this task, and the pane's own button opens the editor. */

    const onProject = linkedProjectCount > 0;
    const onMatter = !!taskDetails?.matter?.uuid;

    actions.push({
      label: t('Link to project'),
      onClick: handleLinkToProject,
      icon: <PiProjectorScreenChartLight className="w-4 h-4" />,
      disabled: onProject,
      title: onProject ? t('Already linked to a project') : undefined,
      variant: 'outline',
    });

    actions.push({
      label: t('Link to matter'),
      onClick: handleLinkToMatter,
      icon: <PiSuitcaseSimpleLight className="w-4 h-4" />,
      disabled: onMatter,
      title: onMatter ? t('Already linked to a matter') : undefined,
      variant: 'outline',
    });

    if (taskStatus === 'todo' && userAccessLevel !== 'view') {
      actions.push({
        label: t('Archive'),
        onClick: handleArchive,
        icon: <PiArchiveLight className="w-4 h-4 text-danger-900" />,
        variant: 'destructive',
      });
    }

    return actions;
  }, [
    userAccessLevel,
    t,
    taskStatus,
    handleArchive,
    handleLinkToProject,
    handleLinkToMatter,
    linkedProjectCount,
    taskDetails?.matter?.uuid,
  ]);

  const additionalHeaderElements = useMemo(() => (
    <div className="flex gap-3 items-center" data-tour-id="task-detail-collaborators">
      <ProjectBadges
        key={projectBadgeNonce}
        resourceType="task"
        resourceUuid={taskUuid}
        /* A task on a matter is linked, so the badges keep the quiet "Add to
           project" plus rather than claiming the task is not linked at all. */
        hasOtherLink={!!taskDetails?.matter?.uuid}
        onProjectCountChange={setLinkedProjectCount}
      />
      <PendingAccessRequests resourceType="task" resourceUuid={taskUuid} />
      {/* The stack is the way into the pane that holds the whole list. It was
          a hand-rolled run of `CollaboratorBadge`s with its own casing chain
          and its own "+N"; it is the shared one now - see
          `shared/access/CollaboratorStack`. */}
      <CollaboratorStack
        collaborators={collaborators}
        onClick={() => surfaceApi.openPane('access')}
        testId="task-collaborator-stack"
      />
    </div>
  ), [collaborators, taskUuid, surfaceApi, taskDetails?.matter?.uuid, projectBadgeNonce]);

  const subHeaderRight = useMemo(() => taskDetails ? (
    <div className="flex flex-wrap gap-2 items-center" data-tour-id="task-detail-badges">
      <Badge
        color={taskStatusColor(taskStatus) as any}
        icon={<PiCheckLight />}
        className="capitalize"
      >
        {taskStatus === 'deleted' ? t('ARCHIVED') : formatStatus(taskStatus)}
      </Badge>
      <Badge
        color={taskPriorityColor(taskDetails.priority) as any}
        icon={<PiFlagLight />}
      >
        {taskDetails.priority ? formatStatus(taskDetails.priority) : t('Medium')}
      </Badge>
      <Badge
        color="gray"
        icon={<PiCalendarBlankLight />}
      >
        {taskDetails.due_date
          ? formatDateTime(taskDetails.due_date)
          : t('No Due Date')}
      </Badge>
    </div>
  ) : null, [taskDetails, taskStatus, t]);

  return (
    <TaskSurfaceProvider value={surfaceApi}>
    <DetailFrame
      breadcrumbs={taskBreadcrumbs}
      actions={taskActions}
      dropdownActions={headerDropdownActions}
      renderDropdownAsButtons={false}
      subscriptionActive={subscriptionActive}
      showBackButton={true}
      subHeaderRight={subHeaderRight}
      additionalHeaderElements={additionalHeaderElements}
      notFound={accessDenied}
    >
      {loading ? (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-shrink-0 w-full">
            <DetailTabBar tabs={tabs} activeTab="Details" onTabClick={() => {}} />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
            <Skeleton variant="detail" />
          </div>
        </div>
      ) : !taskDetails ? null : (
        <div className="flex-1 flex flex-col min-h-0">
          {/*
            THE TABS ARE THEIR OWN ROW, FULL WIDTH, ABOVE THE SURFACE - so a
            docked pane floats below them rather than over them, and the row
            does not shrink as panes open.
          */}
          <div data-tour-id="task-detail-tabs" className="flex-shrink-0 w-full">
            <DetailTabBar
              tabs={tabs}
              activeTab={activeTab}
              onTabClick={goToTab}
              tourId="task-detail-tabs-left"
              /* Tabs left, pane toggles right, one row. `labels="auto"` drops
                 the rail to icons below xl so a laptop shares the row instead
                 of clipping it. */
              actions={(
                <PaneRail
                  panels={panels}
                  ariaLabel={t('Panels')}
                  labels="auto"
                  /* Straight from the surface config: every pane that asked
                     for a rail toggle gets one, in declaration order, so a new
                     pane cannot be added and then forgotten here. */
                  items={TASK_SURFACE.railItems({
                    t,
                    icons: {
                      context: <PiInfoLight />,
                      access: <PiUsersLight />,
                      activity: <PiClockCounterClockwiseLight />,
                    },
                    badges: { access: collaborators.length },
                  })}
                />
              )}
            />
          </div>

          <DetailPageSurface
            surface={TASK_SURFACE}
            layoutApi={panels}
            panes={[
              {
                id: 'context',
                title: t('Context'),
                icon: <PiInfoLight />,
                body: (
                  <TaskContextPane
                    taskDetails={taskDetails}
                    taskStatus={taskStatus}
                    collaborators={collaborators}
                    onOpenAccess={() => surfaceApi.openPane('access')}
                  />
                ),
              },
              {
                id: 'access',
                title: t('Access'),
                icon: <PiUsersLight />,
                body: (
                  <AccessPane
                    collaborators={collaborators}
                    onManageAccess={handleManageCollaborators}
                    canManage={userAccessLevel !== 'view'}
                  />
                ),
              },
              {
                id: 'activity',
                title: t('Activity'),
                icon: <PiClockCounterClockwiseLight />,
                /* A thunk: the log fetches on mount, so it is built only
                   while the pane is actually on screen. No `hideHeader`
                   either - it reads `useInPane()` and renders its body
                   alone. */
                body: () => (
                  <PaneBody>
                    <AuditLogPanel
                      entityType="task"
                      entityId={taskUuid}
                      entityName={taskDetails?.title || ''}
                      /* THE FEED AND THE AUDIT TRAIL ARE TWO STORES.
                         A feed post is written to the task's own SQL feed
                         table and never reaches the audit trail, so posting
                         used to leave Activity silent - which reads as a lost
                         post. The two are merged on this rail by timestamp
                         until the backend emits an audit row on feed post. */
                      extraEntries={feedActivityEntries}
                      onRefreshExtra={loadTaskFeed}
                      onClose={() => panels.setVisible('activity', false)}
                    />
                  </PaneBody>
                ),
              },
            ]}
          >
            {/* Each tab claims the flex space in its own way - the Feed is
                `h-full overflow-hidden` and must not sit in a scroller. See
                `TaskDetailsContent`. */}
            <TaskDetailsContent
              activeTab={activeTab}
              taskDetails={taskDetails}
              taskUuid={taskUuid}
              userAccessLevel={userAccessLevel}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              formik={formik}
              hasChanges={hasChanges}
              setHasChanges={setHasChanges}
              onSubmit={() => formik.handleSubmit()}
              taskStatus={taskStatus}
              comments={comments}
              setComments={setComments}
              collaborators={collaborators}
              setCollaborators={setCollaborators}
              attachments={attachments}
              setAttachments={setAttachments}
              matterData={matterData}
              setMatterData={setMatterData}
              commentsLoading={commentsLoading}
              collaboratorsLoading={collaboratorsLoading}
              attachmentsLoading={attachmentsLoading}
              matterLoading={matterLoading}
              newComment={newComment}
              setNewComment={setNewComment}
              selectedCaseId={selectedCaseId}
              setSelectedCaseId={setSelectedCaseId}
              matters={matters}
              refetchTabData={refetchTabData}
              handleAddComment={handleAddComment}
              handleOpenUploadModal={handleOpenUploadModal}
              handleFileUpload={handleFileUpload}
              handleOpenAttachment={handleOpenAttachment}
              handleDeleteAttachment={handleDeleteAttachment}
              handleChangeMatter={handleChangeMatter}
              onOpenAuditLog={() => surfaceApi.openPane('activity')}
              taskFeed={taskFeed}
              taskFeedLoading={taskFeedLoading}
              onRefreshFeed={loadTaskFeed}
              onPostFeedEvent={handlePostTaskFeedEvent}
              onFeedCommentAdd={handleAddTaskFeedComment}
              currentUser={userInfo ? {
                id: userInfo.UserID || 'current-user',
                first_name: userInfo.FirstName || 'You',
                last_name: userInfo.LastName || '',
                email: userInfo.Email || '',
              } : undefined}
              feedMembers={(collaborators || []).map((c: any) => ({
                id: c.Email || c.id,
                first_name: c.Name?.split(' ')[0] || c.first_name || '',
                last_name: c.Name?.split(' ').slice(1).join(' ') || c.last_name || '',
                email: c.Email || c.email || '',
              }))}
            />
          </DetailPageSurface>
        </div>
      )}
      <FeatureTour
        steps={taskDetailTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </DetailFrame>
    </TaskSurfaceProvider>
  );
};

export default TaskDetail;
